import {
  Button,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getLocalizedDate } from '$shared/utils/date.utils';

import { IncidentsStatusBadge } from '$features/incidents-list/incidents-status';
import { getEventIconName } from '$features/monitoring/events/events.utils';

import { useDashboardIncidents } from '../dashboard.api';

import { DashboardCardTitle } from './dashboard-card-title';
import { DashboardWidget } from './dashboard-widget';
import { SectionFeedback } from './section-feedback';
import { TruncatedText } from './truncated-text';

import './alerts-incidents-card.scss';

export function AlertsIncidentsCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useDashboardIncidents();

  return (
    <DashboardWidget className="alerts-incidents-card">
      <div className="dashboard-widget__header">
        <DashboardCardTitle
          icon="alert"
          tone="orange"
          title={t('dashboard.alertsIncidents.title')}
          hint={t('dashboard.alertsIncidents.hint')}
          subheadline={t('dashboard.alertsIncidents.period')}
        />
        <SectionFeedback sectionId="alerts-incidents" />
      </div>
      {isLoading && <DotsLoader className="m-top-l" />}
      {isError && (
        <div className="alerts-incidents-card__feedback" role="alert">
          <span>{t('dashboard.alertsIncidents.loadError')}</span>
          <Button
            variant="secondary"
            onClick={() => {
              refetch();
            }}
          >
            {t('dashboard.alertsIncidents.retry')}
          </Button>
        </div>
      )}
      {!isLoading && !isError && (!data || data.length === 0) && (
        <div className="alerts-incidents-card__empty">
          {t('dashboard.alertsIncidents.noIncidents')}
        </div>
      )}
      {!isLoading && !isError && data && data.length > 0 && (
        <div className="alerts-incidents-card__table">
          {data.map((incident) => (
            <Link
              key={incident.id}
              className="alerts-incidents-card__row"
              to={{
                search: `?sidecarRoute=incident/flow&id=${incident.id}`,
              }}
            >
              <div className="alerts-incidents-card__type">
                <Icon
                  name={`event-${getEventIconName(incident.severity)}`}
                  size={IconSizeClasses.XSmall}
                />
              </div>
              <TruncatedText
                text={incident.description}
                className="alerts-incidents-card__description"
              />
              <div className="alerts-incidents-card__device-details">
                <TruncatedText
                  text={incident.deviceName}
                  className="alerts-incidents-card__device"
                />
                {incident.location && (
                  <TruncatedText
                    text={incident.location}
                    className="alerts-incidents-card__location"
                  />
                )}
              </div>
              <time
                className="alerts-incidents-card__time"
                dateTime={new Date(incident.timestamp).toISOString()}
              >
                {getLocalizedDate(incident.timestamp)}
              </time>
              <div className="alerts-incidents-card__status">
                <IncidentsStatusBadge
                  incidentStatus={incident.incidentStatus}
                  hasBorder={false}
                  size="small"
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
}
